/**
 * Single source of truth for DDX-Plus temporal / graph enums.
 * Import from here — do not hardcode these strings in other modules.
 */

/** @type {Readonly<Record<string, string>>} */
export const TEMPORAL_BUCKETS = Object.freeze({
  HYPERACUTE: 'hyperacute',
  ACUTE: 'acute',
  SUBACUTE: 'subacute',
  CHRONIC: 'chronic',
  INDETERMINATE: 'indeterminate',
});

/** Ordered for adjacent-bucket scoring (hyperacute … chronic) */
export const TEMPORAL_BUCKET_ORDER = Object.freeze([
  TEMPORAL_BUCKETS.HYPERACUTE,
  TEMPORAL_BUCKETS.ACUTE,
  TEMPORAL_BUCKETS.SUBACUTE,
  TEMPORAL_BUCKETS.CHRONIC,
]);

/** @type {Readonly<Record<string, string>>} */
export const ONSET_PATTERNS = Object.freeze({
  SUDDEN: 'sudden',
  RAPID: 'rapid',
  GRADUAL: 'gradual',
  INSIDIOUS: 'insidious',
});

/** @type {Readonly<Record<string, string>>} */
export const FEATURE_STATUS = Object.freeze({
  PRESENT: 'present',
  ABSENT: 'absent',
  RESOLVED: 'resolved',
  HISTORICAL: 'historical',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const FEATURE_TYPES = Object.freeze({
  SYMPTOM: 'symptom',
  HISTORY_MEDICAL: 'history_medical',
  HISTORY_SURGICAL: 'history_surgical',
  HISTORY_FAMILY: 'history_family',
  HISTORY_SOCIAL: 'history_social',
  HISTORY_MEDICATION: 'history_medication',
  EXAM_VITAL: 'exam_vital',
  EXAM_GENERAL: 'exam_general',
  EXAM_FOCUSED: 'exam_focused',
  DIAGNOSTIC_LAB: 'diagnostic_lab',
  DIAGNOSTIC_IMAGING: 'diagnostic_imaging',
  DIAGNOSTIC_ECG: 'diagnostic_ecg',
  DIAGNOSTIC_OTHER: 'diagnostic_other',
});

/** Semantic anchors for edge strength (0–1); not hard bins */
export const EDGE_STRENGTH_ANCHORS = Object.freeze({
  PATHOGNOMONIC: 1.0,
  VERY_STRONG: 0.85,
  STRONG: 0.7,
  MODERATE: 0.5,
  WEAK: 0.3,
  INCIDENTAL: 0.1,
});

/** @type {Readonly<Record<string, string>>} */
export const CLINICAL_SIGNIFICANCE = Object.freeze({
  RULE_IN: 'rule_in',
  RULE_OUT_IF_ABSENT: 'rule_out_if_absent',
  DISCRIMINATOR: 'discriminator',
  RISK_FACTOR: 'risk_factor',
  INCIDENTAL: 'incidental',
});

/** @type {Readonly<Record<string, string>>} */
export const AGE_BANDS = Object.freeze({
  PEDIATRIC: 'pediatric',
  YOUNG_ADULT: 'young_adult',
  ADULT: 'adult',
  ELDERLY: 'elderly',
});

/** @type {Readonly<Record<string, number>>} */
export const TRUST_TIERS = Object.freeze({
  TIER_1: 1,
  TIER_2: 2,
  TIER_3: 3,
});

/** @type {Readonly<Record<string, string>>} */
export const VERIFICATION_STATUS = Object.freeze({
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified',
});

/** @type {Readonly<Record<string, string>>} */
export const DDX_FEEDBACK_EVENT_TYPES = Object.freeze({
  DX_ACCEPTED: 'dx_accepted',
  DX_REJECTED: 'dx_rejected',
  DX_RERANKED: 'dx_reranked',
  DX_ADDED: 'dx_added',
  CANT_MISS_ACTIONED: 'cant_miss_actioned',
  CANT_MISS_DISMISSED: 'cant_miss_dismissed',
  SESSION_COMPLETED: 'session_completed',
});

/** Phase 2+ trust labels; consumed by trustMultiplierForDiagnosis in ddxKbProjection.js (single source of truth). */
export const TRUST_VERIFICATION_MULTIPLIERS = Object.freeze({
  '1_verified': 1.0,
  '2_verified': 0.85,
  '2_unverified': 0.55,
});

/** MC-2: clamp edge contribution before summing */
export const EDGE_STRENGTH_CLAMP = Object.freeze({ min: 0.05, max: 0.95 });

/** Approximate token budget for serialized KB bundle text */
export const KB_BUNDLE_TOKEN_BUDGET = 3000;

// ── Patient context enums (DDX-Plus) — keep in sync with PatientContextProfile / LLM schema

/** @type {Readonly<Record<string, string>>} */
export const BIOLOGICAL_SEX = Object.freeze({
  MALE: 'male',
  FEMALE: 'female',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const SMOKING_STATUS = Object.freeze({
  NEVER: 'never',
  FORMER: 'former',
  CURRENT_LIGHT: 'current_light',
  CURRENT_HEAVY: 'current_heavy',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const ALCOHOL_USE = Object.freeze({
  NONE: 'none',
  MODERATE: 'moderate',
  HEAVY: 'heavy',
  BINGE: 'binge',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const DRUG_USE = Object.freeze({
  NONE: 'none',
  COCAINE: 'cocaine',
  STIMULANTS: 'stimulants',
  OPIOIDS: 'opioids',
  CANNABIS: 'cannabis',
  IVDU: 'ivdu',
  OTHER: 'other',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const HOUSING_STATUS = Object.freeze({
  HOUSED: 'housed',
  UNSTABLE: 'unstable',
  UNHOUSED: 'unhoused',
  INCARCERATED: 'incarcerated',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const OCCUPATION_RISK = Object.freeze({
  NONE_KNOWN: 'none_known',
  HEALTHCARE: 'healthcare',
  FARMING: 'farming',
  CONSTRUCTION: 'construction',
  MINING: 'mining',
  FOOD_SERVICE: 'food_service',
  SEX_WORK: 'sex_work',
  FIRST_RESPONDER: 'first_responder',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const IMMUNOSUPPRESSION_STATUS = Object.freeze({
  NONE: 'none',
  HIV_CONTROLLED: 'hiv_controlled',
  HIV_AIDS: 'hiv_aids',
  TRANSPLANT: 'transplant',
  ACTIVE_CHEMO: 'active_chemo',
  CHRONIC_STEROIDS: 'chronic_steroids',
  BIOLOGIC_THERAPY: 'biologic_therapy',
  PRIMARY_IMMUNODEF: 'primary_immunodef',
  OTHER: 'other',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const PREGNANCY_STATUS = Object.freeze({
  NOT_PREGNANT: 'not_pregnant',
  PREGNANT_FIRST: 'pregnant_first',
  PREGNANT_SECOND: 'pregnant_second',
  PREGNANT_THIRD: 'pregnant_third',
  POSTPARTUM: 'postpartum',
  UNKNOWN: 'unknown',
  NOT_APPLICABLE: 'not_applicable',
});

/** @type {Readonly<Record<string, string>>} */
export const ANTICOAGULATION_STATUS = Object.freeze({
  NONE: 'none',
  WARFARIN: 'warfarin',
  DOAC: 'doac',
  HEPARIN: 'heparin',
  ANTIPLATELET: 'antiplatelet',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const OBESITY_STATUS = Object.freeze({
  NORMAL_OR_UNKNOWN: 'normal_or_unknown',
  OVERWEIGHT: 'overweight',
  OBESE: 'obese',
  SEVERELY_OBESE: 'severely_obese',
  POST_BARIATRIC: 'post_bariatric',
});

/** @type {Readonly<Record<string, string>>} */
export const FAMILY_HISTORY_FLAGS = Object.freeze({
  PREMATURE_CAD: 'premature_cad',
  CLOTTING_DISORDER: 'clotting_disorder',
  FAMILIAL_HYPERCHOLEST: 'familial_hypercholest',
  SUDDEN_CARDIAC_DEATH: 'sudden_cardiac_death',
  HEREDITARY_CANCER: 'hereditary_cancer',
  NONE_KNOWN: 'none_known',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const PRIOR_EPISODE_STATUS = Object.freeze({
  FIRST_EPISODE: 'first_episode',
  RECURRENT: 'recurrent',
  KNOWN_CHRONIC: 'known_chronic',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const RELEVANT_COMORBIDITIES = Object.freeze({
  NONE_KNOWN: 'none_known',
  CAD: 'cad',
  HEART_FAILURE: 'heart_failure',
  AFIB: 'afib',
  COPD: 'copd',
  ASTHMA: 'asthma',
  DIABETES: 'diabetes',
  CKD: 'ckd',
  CIRRHOSIS: 'cirrhosis',
  MALIGNANCY: 'malignancy',
  PRIOR_STROKE: 'prior_stroke',
  PRIOR_PE_DVT: 'prior_pe_dvt',
  PRIOR_MI: 'prior_mi',
  HYPERTENSION: 'hypertension',
  CONNECTIVE_TISSUE: 'connective_tissue',
  SICKLE_CELL: 'sickle_cell',
  EPILEPSY: 'epilepsy',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const VACCINATION_GAPS = Object.freeze({
  NONE_KNOWN: 'none_known',
  INFLUENZA: 'influenza',
  COVID: 'covid',
  MENINGOCOCCAL: 'meningococcal',
  PNEUMOCOCCAL: 'pneumococcal',
  HPV: 'hpv',
  HEPATITIS_B: 'hepatitis_b',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const TRAVEL_HISTORY = Object.freeze({
  NONE: 'none',
  DOMESTIC: 'domestic',
  TROPICAL: 'tropical',
  ENDEMIC_TB: 'endemic_tb',
  ENDEMIC_ZIKA: 'endemic_zika',
  ENDEMIC_HANTA: 'endemic_hanta',
  ENDEMIC_COCCIDIOIDES: 'endemic_coccidioides',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const SEXUAL_HISTORY_FLAGS = Object.freeze({
  NO_RISK_FACTORS: 'no_risk_factors',
  MULTIPLE_PARTNERS: 'multiple_partners',
  NEW_PARTNER: 'new_partner',
  MSM: 'msm',
  UNPROTECTED: 'unprotected',
  UNKNOWN: 'unknown',
  NOT_DISCLOSED: 'not_disclosed',
});

/** @type {Readonly<Record<string, string>>} */
export const HIGH_IMPACT_MEDICATIONS = Object.freeze({
  NONE_KNOWN: 'none_known',
  NSAIDS_CHRONIC: 'nsaids_chronic',
  STEROIDS_CHRONIC: 'steroids_chronic',
  ANTIPSYCHOTICS: 'antipsychotics',
  TRICYCLICS: 'tricyclics',
  DIURETICS: 'diuretics',
  ACE_INHIBITORS: 'ace_inhibitors',
  DIGOXIN: 'digoxin',
  LITHIUM: 'lithium',
  METFORMIN: 'metformin',
  OCP: 'ocp',
  STIMULANT_MEDS: 'stimulant_meds',
  IMMUNOSUPPRESSANTS: 'immunosuppressants',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const MECHANISM_OF_INJURY = Object.freeze({
  NONE: 'none',
  LOW_ENERGY: 'low_energy',
  HIGH_ENERGY: 'high_energy',
  PENETRATING: 'penetrating',
  BLAST: 'blast',
  UNKNOWN: 'unknown',
});

/** @type {Readonly<Record<string, string>>} */
export const CONTEXT_FACTOR_WEIGHT = Object.freeze({
  RESHAPES_DIFFERENTIAL: 'reshapes_differential',
  STRONG_MODIFIER: 'strong_modifier',
  MODERATE_MODIFIER: 'moderate_modifier',
  WEAK_MODIFIER: 'weak_modifier',
});

/** @type {Readonly<Set<string>>} */
const _TEMPORAL_SET = new Set(Object.values(TEMPORAL_BUCKETS));
/** @type {Readonly<Set<string>>} */
const _ONSET_SET = new Set(Object.values(ONSET_PATTERNS));
/** @type {Readonly<Set<string>>} */
const _FEATURE_STATUS_SET = new Set(Object.values(FEATURE_STATUS));
/** @type {Readonly<Set<string>>} */
const _FEATURE_TYPE_SET = new Set(Object.values(FEATURE_TYPES));
/** @type {Readonly<Set<string>>} */
const _AGE_SET = new Set(Object.values(AGE_BANDS));
/** @type {Readonly<Set<string>>} */
const _BIOLOGICAL_SEX_SET = new Set(Object.values(BIOLOGICAL_SEX));
/** @type {Readonly<Set<string>>} */
const _SMOKING_SET = new Set(Object.values(SMOKING_STATUS));
/** @type {Readonly<Set<string>>} */
const _ALCOHOL_SET = new Set(Object.values(ALCOHOL_USE));
/** @type {Readonly<Set<string>>} */
const _DRUG_USE_SET = new Set(Object.values(DRUG_USE));
/** @type {Readonly<Set<string>>} */
const _HOUSING_SET = new Set(Object.values(HOUSING_STATUS));
/** @type {Readonly<Set<string>>} */
const _OCCUPATION_SET = new Set(Object.values(OCCUPATION_RISK));
/** @type {Readonly<Set<string>>} */
const _IMMUNO_SET = new Set(Object.values(IMMUNOSUPPRESSION_STATUS));
/** @type {Readonly<Set<string>>} */
const _PREGNANCY_SET = new Set(Object.values(PREGNANCY_STATUS));
/** @type {Readonly<Set<string>>} */
const _ANTICOAG_SET = new Set(Object.values(ANTICOAGULATION_STATUS));
/** @type {Readonly<Set<string>>} */
const _OBESITY_SET = new Set(Object.values(OBESITY_STATUS));
/** @type {Readonly<Set<string>>} */
const _FAMHX_SET = new Set(Object.values(FAMILY_HISTORY_FLAGS));
/** @type {Readonly<Set<string>>} */
const _PRIOR_EP_SET = new Set(Object.values(PRIOR_EPISODE_STATUS));
/** @type {Readonly<Set<string>>} */
const _COMORB_SET = new Set(Object.values(RELEVANT_COMORBIDITIES));
/** @type {Readonly<Set<string>>} */
const _VAC_GAP_SET = new Set(Object.values(VACCINATION_GAPS));
/** @type {Readonly<Set<string>>} */
const _TRAVEL_SET = new Set(Object.values(TRAVEL_HISTORY));
/** @type {Readonly<Set<string>>} */
const _SEXUAL_SET = new Set(Object.values(SEXUAL_HISTORY_FLAGS));
/** @type {Readonly<Set<string>>} */
const _HI_MED_SET = new Set(Object.values(HIGH_IMPACT_MEDICATIONS));
/** @type {Readonly<Set<string>>} */
const _MECH_SET = new Set(Object.values(MECHANISM_OF_INJURY));

/**
 * Map extractor duration phrases to TEMPORAL_BUCKETS values.
 * @param {string} raw
 * @returns {string | null}
 */
export function coerceDurationToTemporalBucket(raw) {
  if (raw == null || typeof raw !== 'string') return null;
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  const synonyms = {
    [TEMPORAL_BUCKETS.HYPERACUTE]: ['hyperacute', 'seconds', 'minutes', 'instant', 'sudden onset immediate'],
    [TEMPORAL_BUCKETS.ACUTE]: ['acute', 'hours', 'hours_to_day', 'today', 'same day', 'overnight'],
    [TEMPORAL_BUCKETS.SUBACUTE]: ['subacute', 'days', 'few days', 'week'],
    [TEMPORAL_BUCKETS.CHRONIC]: ['chronic', 'weeks', 'months', 'years', 'ongoing'],
    [TEMPORAL_BUCKETS.INDETERMINATE]: ['indeterminate', 'unknown', 'unclear'],
  };
  for (const [bucket, keys] of Object.entries(synonyms)) {
    if (keys.some((k) => s === k || s.includes(k))) return bucket;
  }
  if (_TEMPORAL_SET.has(s)) return s;
  return TEMPORAL_BUCKETS.INDETERMINATE;
}

/**
 * @param {unknown} v
 * @returns {string | null}
 */
export function normalizeTemporalBucket(v) {
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  if (_TEMPORAL_SET.has(s)) return s;
  return coerceDurationToTemporalBucket(s);
}

/**
 * @param {unknown} v
 * @returns {string | null}
 */
export function normalizeOnsetPattern(v) {
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  if (_ONSET_SET.has(s)) return s;
  return null;
}

/**
 * @param {unknown} v
 * @returns {string}
 */
export function normalizeFeatureStatus(v) {
  if (v == null) return FEATURE_STATUS.UNKNOWN;
  const s = String(v).trim().toLowerCase();
  if (_FEATURE_STATUS_SET.has(s)) return s;
  return FEATURE_STATUS.UNKNOWN;
}

/**
 * @param {unknown} v
 * @returns {string | null}
 */
export function normalizeFeatureType(v) {
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  if (_FEATURE_TYPE_SET.has(s)) return s;
  return null;
}

/**
 * @param {unknown} v
 * @returns {string | null}
 */
export function normalizeAgeBand(v) {
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  if (_AGE_SET.has(s)) return s;
  if (s === 'peds' || s === 'pediatric') return AGE_BANDS.PEDIATRIC;
  return null;
}

/**
 * @param {unknown} v
 * @param {ReadonlySet<string>} allowed
 * @param {string} fallback
 * @returns {string}
 */
export function normalizePatientContextEnum(v, allowed, fallback) {
  if (v == null || v === '') return fallback;
  const s = String(v).trim().toLowerCase();
  if (allowed.has(s)) return s;
  return fallback;
}

/** @param {unknown} v @returns {string} */
export function normalizeBiologicalSex(v) {
  return normalizePatientContextEnum(v, _BIOLOGICAL_SEX_SET, BIOLOGICAL_SEX.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeSmokingStatus(v) {
  return normalizePatientContextEnum(v, _SMOKING_SET, SMOKING_STATUS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeAlcoholUse(v) {
  return normalizePatientContextEnum(v, _ALCOHOL_SET, ALCOHOL_USE.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeDrugUseItem(v) {
  return normalizePatientContextEnum(v, _DRUG_USE_SET, DRUG_USE.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeHousingStatus(v) {
  return normalizePatientContextEnum(v, _HOUSING_SET, HOUSING_STATUS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeOccupationRisk(v) {
  return normalizePatientContextEnum(v, _OCCUPATION_SET, OCCUPATION_RISK.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeImmunosuppressionStatus(v) {
  return normalizePatientContextEnum(v, _IMMUNO_SET, IMMUNOSUPPRESSION_STATUS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizePregnancyStatus(v) {
  return normalizePatientContextEnum(v, _PREGNANCY_SET, PREGNANCY_STATUS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeAnticoagulationStatus(v) {
  return normalizePatientContextEnum(v, _ANTICOAG_SET, ANTICOAGULATION_STATUS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeObesityStatus(v) {
  return normalizePatientContextEnum(v, _OBESITY_SET, OBESITY_STATUS.NORMAL_OR_UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeFamilyHistoryFlagItem(v) {
  return normalizePatientContextEnum(v, _FAMHX_SET, FAMILY_HISTORY_FLAGS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizePriorEpisodeStatus(v) {
  return normalizePatientContextEnum(v, _PRIOR_EP_SET, PRIOR_EPISODE_STATUS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeRelevantComorbidityItem(v) {
  return normalizePatientContextEnum(v, _COMORB_SET, RELEVANT_COMORBIDITIES.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeVaccinationGapItem(v) {
  return normalizePatientContextEnum(v, _VAC_GAP_SET, VACCINATION_GAPS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeTravelHistory(v) {
  return normalizePatientContextEnum(v, _TRAVEL_SET, TRAVEL_HISTORY.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeSexualHistoryFlagItem(v) {
  return normalizePatientContextEnum(v, _SEXUAL_SET, SEXUAL_HISTORY_FLAGS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeHighImpactMedicationItem(v) {
  return normalizePatientContextEnum(v, _HI_MED_SET, HIGH_IMPACT_MEDICATIONS.UNKNOWN);
}

/** @param {unknown} v @returns {string} */
export function normalizeMechanismOfInjury(v) {
  return normalizePatientContextEnum(v, _MECH_SET, MECHANISM_OF_INJURY.UNKNOWN);
}
